<template>
  <div>
    <v-card v-if="!this.dense" height="100%" class="attachment flex d-flex flex-column" flat>
      <v-card-text class="flex">
        <div class="font font--label-light">
          <div class="d-flex justify-center mb-2">
            <div :class="['fiv-cla', this.iconSizeClass, getIcon(attachment.mime)]"></div>
          </div>
          <div class="text-center mb-2">
            <a @click.stop="prepareFileLinkForDownload" :href="buildFileUrl()" target="_blank" rel="noopener">{{ filenameOrLabel }}</a>
            <slot name="default" v-bind="{ attachment, label }">
              <div v-if="label" class="pt-3">{{ label }}</div>
            </slot>
          </div>
        </div>
      </v-card-text>
      <slot name="actions" v-bind="{ attachment, label }">
        <v-card-actions>
          <v-spacer></v-spacer>
          <slot name="buttons" v-bind="{ attachment }">
            <slot name="buttons-append" v-bind="{ attachment }"></slot>

            <v-btn v-if="attachment.id" color="error" :disabled="!attachment.can_be_deleted" @click.stop="onAttachmentRemove" icon text>
              <v-icon>mdi-trash-can-outline</v-icon>
            </v-btn>

            <slot name="buttons-prepend" v-bind="{ attachment }"></slot>
          </slot>
        </v-card-actions>
      </slot>
    </v-card>
    <div v-else class="d-inline-block">
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <a
            v-bind="attrs" v-on="on"
            @click.stop="prepareFileLinkForDownload" :href="buildFileUrl()" target="_blank" rel="noopener"
          >
            <div :class="['text-decoration-none', 'fiv-cla', iconSizeClass, getIcon(attachment.mime)]"></div>
          </a>
        </template>

        <span>
          {{ filenameOrLabel }}
        </span>
      </v-tooltip>
    </div>
  </div>
</template>

<script>
import {attachmentPrepareLink, attachmentDelete} from "@/api/attachment";
import icons from "file-icon-vectors/dist/icons/classic/catalog.json";
import * as mimeDb from "mime-db";

/**
 * @typedef Attachment
 * @property {string|null} id
 * @property {string|null} url
 * @property {string} mime
 * @property {string} filename
 * @property {boolean} can_be_deleted
 */

export default {
  name: "AttachmentItem",
  data: () => ({
    validTo: null,
    preparedUrl: null,
    prepareLinkProcess: false
  }),

  props: {
    /** @type {import('vue').PropType<Attachment>} */
    attachment: {
      type: Object,
      required: true
    },
    label: String,
    dense: {
      type: Boolean,
      default: false
    },
    iconSizeClass: {
      type: String,
      default: 'fiv-size-lg'
    }
  },

  computed: {
    filenameOrLabel() {
      return this.attachment.filename ?? this.label;
    }
  },

  methods: {
    async onAttachmentRemove(event) {
      const response = await attachmentDelete(this.attachment.id);
      if (response) {
        this.$emit('remove', { attachment: this.attachment, response });
      }
    },

    async prepareFileLinkForDownload(event)
    {
      if (this.prepareLinkProcess) {
        return;
      }

      if (!this.isFileLinkValid())
      {
        this.prepareLinkProcess = true;

        event.preventDefault();

        this.preparedUrl = this.validTo = null;

        const res = await attachmentPrepareLink(this.attachment.id);
        if (res)
        {
          this.preparedUrl = res.url.public;
          this.validTo = res.valid_to ? new Date(res.valid_to) : null;

          if (this.preparedUrl && event.target) {
            this.$nextTick(() => {
              event.target.click();
              this.prepareLinkProcess = false;
            });
          } else {
            this.prepareLinkProcess = false;
          }
        } else {
          this.prepareLinkProcess = false;
        }
      }
    },

    buildFileUrl() {
      if (this.attachment.url) {
        return this.attachment.url;
      }

      if (!this.isFileLinkValid()) {
        return "#";
      }

      return this.preparedUrl;
    },

    isFileLinkValid() {
      if (this.attachment.url) {
        return true;
      }

      return this.preparedUrl && (this.validTo === null || this.validTo > (new Date()));
    },

    getIcon(mimeType) {
      const iconName = (mimeDb[mimeType]?.extensions?.find((extension) => icons.includes(extension))) ?? 'blank';
      return "fiv-icon-" + iconName;
    }
  }
}
</script>

<style scoped>
  .attachment {
    background: #f5f5f6;
  }
</style>
